<template>
  <div class="campaign-form-wrapper">
    <!-- Inline Auth Container -->
    <div v-if="showInlineAuth" class="inline-auth-section">
      <inline-auth 
        :show="showInlineAuth"
        :user-exists="userEmailExists"
        :user-email="userEmail"
        @auth-success="handleAuthSuccess"
        @show-forgot-password="handleForgotPassword"
      />
    </div>
    
    <!-- Form Content Slot -->
    <slot 
      :form-data="formData"
      :is-authenticated="isAuthenticated"
      :email-checked="emailChecked"
      :show-inline-auth="showInlineAuth"
    />
  </div>
</template>

<script>
export default {
  name: 'CampaignForm',
  
  props: {
    initialAuthenticated: {
      type: Boolean,
      default: false
    },
    initialEmail: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      formData: {
        isAuthenticated: this.initialAuthenticated,
        userEmail: this.initialEmail,
        userEmailExists: null,
        emailChecked: false,
        showInlineAuth: false,
        emailCheckTimeout: null
      }
    };
  },
  
  computed: {
    isAuthenticated() {
      return this.formData.isAuthenticated;
    },
    
    userEmail() {
      return this.formData.userEmail;
    },
    
    userEmailExists() {
      return this.formData.userEmailExists;
    },
    
    emailChecked() {
      return this.formData.emailChecked;
    },
    
    showInlineAuth() {
      return this.formData.showInlineAuth;
    }
  },
  
  methods: {
    // Email validation and checking
    async checkEmail(email) {
      if (this.formData.emailCheckTimeout) {
        clearTimeout(this.formData.emailCheckTimeout);
      }
      
      this.formData.userEmail = email;
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.formData.emailChecked = false;
        this.formData.showInlineAuth = false;
        return { valid: false, message: 'Please enter a valid email address' };
      }
      
      // Debounce the email check
      return new Promise((resolve) => {
        this.formData.emailCheckTimeout = setTimeout(async () => {
          try {
            const response = await fetch('/api/check-email', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
              },
              body: JSON.stringify({ email })
            });
            
            const data = await response.json();
            
            this.formData.userEmailExists = data.exists;
            this.formData.emailChecked = true;
            this.formData.showInlineAuth = true;
            
            resolve({
              valid: true,
              exists: data.exists,
              message: data.exists ? 'Account found! Please sign in to continue.' : 'Email available! Create your account below.'
            });
          } catch (error) {
            console.error('Email check failed:', error);
            resolve({
              valid: true,
              exists: false,
              message: 'Unable to check email. You can continue with the form.'
            });
          }
        }, 1000); // 1 second debounce
      });
    },
    
    // Show inline auth
    showAuth() {
      this.formData.showInlineAuth = true;
    },
    
    // Hide inline auth
    hideAuth() {
      this.formData.showInlineAuth = false;
      this.formData.emailChecked = false;
    },
    
    // Handle successful authentication
    handleAuthSuccess(user) {
      this.formData.isAuthenticated = true;
      this.formData.showInlineAuth = false;
      
      // Update CSRF token if provided
      if (user.csrf_token) {
        document.querySelector('meta[name="csrf-token"]').content = user.csrf_token;
      }
      
      // Emit event for parent handling
      this.$emit('auth-success', user);
      
      // Update global auth state if needed
      if (window.updateAuthUI) {
        window.updateAuthUI(true);
      }
    },
    
    // Handle forgot password
    handleForgotPassword() {
      if (window.openAuthModal) {
        window.openAuthModal('forgot', this.formData.userEmail);
      } else {
        window.location.href = '/password/reset?email=' + encodeURIComponent(this.formData.userEmail);
      }
    },
    
    // Get current form data
    getFormData() {
      return { ...this.formData };
    },
    
    // Update form data
    updateFormData(updates) {
      Object.assign(this.formData, updates);
    }
  },
  
  mounted() {
    // Expose methods to window for backward compatibility
    window.campaignFormMethods = {
      checkEmail: this.checkEmail.bind(this),
      showAuth: this.showAuth.bind(this),
      hideAuth: this.hideAuth.bind(this),
      handleAuthSuccess: this.handleAuthSuccess.bind(this),
      handleForgotPassword: this.handleForgotPassword.bind(this),
      getFormData: this.getFormData.bind(this),
      updateFormData: this.updateFormData.bind(this)
    };
    
    // Also expose the reactive data
    window.campaignFormData = this.formData;
  },
  
  beforeUnmount() {
    // Clean up timeouts
    if (this.formData.emailCheckTimeout) {
      clearTimeout(this.formData.emailCheckTimeout);
    }
    
    // Clean up window references
    delete window.campaignFormMethods;
    delete window.campaignFormData;
  }
};
</script>

<style scoped>
.campaign-form-wrapper {
  position: relative;
}

.inline-auth-section {
  margin-bottom: 1.5rem;
}
</style>